// ClusterMergeReviewOrchestrator
//
// Wraps MergeReviewWizard for N-member clusters. The wizard itself
// is intentionally 2-way (survivor + 1 merged contact) — this
// component handles the cluster-level concerns:
//
//   1. Step 1: pick the survivor among N members.
//   2. Step 2..N: open the 2-way wizard for survivor + each
//      non-survivor in sequence. After each successful merge, refetch
//      the survivor's safe contact (so the next iteration sees any
//      fields that were just promoted from the previous merged side)
//      and advance to the next non-survivor.
//   3. Step N+1: surface the cluster-wide undo toast covering every
//      successful merge in the run.
//
// Replaces ClusterMergeModal entirely. The same external contract
// (cluster + onClose + onComplete) keeps the call sites simple.
//
// For clusters of N=2 (the most common case from the dup-triage
// queue), the survivor-pick step renders inline so the user only
// has to click Survivor → wizard. For N≥3 the survivor-pick step is
// a dedicated screen.

import { useEffect, useMemo, useState } from "react";

import {
  api,
  ApiError,
  type DuplicateCluster,
  type DuplicateContactSide,
  type SafeContact,
} from "../api";
import { Button, Card, ErrorBanner, Pill, Row, Stack } from "../ui";
import { Modal } from "./Modal";
import { useToast } from "./Toast";
import { MergeReviewWizard } from "./MergeReviewWizard";
import {
  countPopulatedFields,
  selectMergeTargets,
  suggestSurvivor,
  type SuggestableContactShape,
} from "./merge-review-helpers";
import { MERGE_TOAST_DURATION_MS } from "./toast-helpers";
import {
  mergedAwayFromError,
  type MergedAwayInfo,
} from "../cleanup/merged-away";

export interface ClusterMergeReviewOrchestratorProps {
  cluster: DuplicateCluster;
  onClose: () => void;
  onComplete: () => void;
  /**
   * Phase 11.x (May 2026 — drop the survivor-picker modal): when
   * provided, the orchestrator skips its pick_survivor phase and
   * jumps straight to merging with this contact as the survivor.
   * The queue page passes the same survivor id that's named on the
   * "Merge into [Name]" button so clicking that button takes the
   * user directly into per-field review with no survivor-picker
   * dialog. To change the survivor mid-cluster the user starts
   * over (Cancel + click a different star on the cards above).
   */
  initialSurvivorId?: string;
  /**
   * Phase 11.x (May 2026): when true, every phase renders without
   * its Modal wrapper so the orchestrator can mount inline in a
   * page's content area (below the comparison cards on the
   * duplicate-candidates queue, for example). Default false (modal).
   */
  inline?: boolean;
}

type Phase =
  | { kind: "pick_survivor" }
  | { kind: "loading_survivor"; survivorId: string }
  | {
      kind: "running";
      survivor: SafeContact;
      // Non-survivor cluster members in the order they'll be merged.
      queue: DuplicateContactSide[];
      // Index of the member currently being merged (0-based).
      cursor: number;
      // Ids of non-survivors that have already been merged successfully.
      succeededIds: string[];
    }
  | { kind: "loading_next"; succeededIds: string[]; nextId: string }
  | {
      kind: "error";
      message: string;
      succeededIds: string[];
      // When the failure is a "record was already merged away" 404, this
      // carries a link to the surviving record so the screen offers a way
      // forward instead of dead-ending (Grant 2026-07-16).
      mergedAway?: MergedAwayInfo | null;
    };

export function ClusterMergeReviewOrchestrator({
  cluster,
  onClose,
  onComplete,
  initialSurvivorId,
  inline = false,
}: ClusterMergeReviewOrchestratorProps) {
  const toast = useToast();
  const memberCount = cluster.members.length;
  // Phase 11.x (May 2026): when an initial survivor is supplied
  // externally (the queue's "Merge into [Name]" button names a
  // survivor before the user even sees the orchestrator), seed the
  // phase machine straight to loading_survivor so the pick_survivor
  // dialog never renders. Otherwise start at pick_survivor as
  // before.
  const seedSurvivorId =
    initialSurvivorId &&
    cluster.members.some((m) => m.id === initialSurvivorId)
      ? initialSurvivorId
      : null;
  const [phase, setPhase] = useState<Phase>(
    seedSurvivorId
      ? { kind: "loading_survivor", survivorId: seedSurvivorId }
      : { kind: "pick_survivor" },
  );
  const [pickedSurvivorId, setPickedSurvivorId] = useState<string>(
    seedSurvivorId ?? cluster.members[0]?.id ?? "",
  );
  // Selective merge (Baker 2026-06-26): in a cluster where only SOME
  // records are the same person — the classic "Michael + Michael +
  // Samantha (his wife, same phone)" — let the steward exclude the
  // members who are different people. Excluded members are kept fully
  // separate; only the survivor + the still-included members merge.
  // Offered on the pick screen for 3+ clusters (a 2-record cluster has
  // nothing to selectively exclude — excluding the one non-survivor
  // would merge nothing). The survivor is never excludable. The seeded
  // queue path (initialSurvivorId, no pick screen) is unaffected — it
  // still merges every non-survivor.
  const [excludedIds, setExcludedIds] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const allowExclude = memberCount >= 3;
  // Non-survivors the steward wants merged in (everyone except the
  // survivor and anyone explicitly kept separate).
  const includedTargetIds = new Set(
    selectMergeTargets(
      cluster.members.map((m) => m.id),
      pickedSurvivorId,
      excludedIds,
    ),
  );
  const includedNonSurvivors = cluster.members.filter((m) =>
    includedTargetIds.has(m.id),
  );
  const excludedMembers = cluster.members.filter((m) =>
    excludedIds.has(m.id),
  );
  // Member detail cache populated lazily. Used by the survivor-pick
  // card to display populated-field counts so the admin can pick the
  // richest record.
  const [detailCache, setDetailCache] = useState<
    Record<string, SafeContact | null>
  >({});

  // Phase B1 (May 2026): once every member's SafeContact is loaded,
  // auto-suggest a survivor based on populated-field count + recency
  // + presence-of-email/phone. The user can override; the suggestion
  // ALSO drives the banner that explains why the wizard recommended
  // that contact.
  const [suggestionApplied, setSuggestionApplied] = useState(false);

  // Lazy-fetch every member's full SafeContact in parallel so the
  // survivor-pick cards can show "X of Y fields populated" + the
  // wizard inherits the cache when it activates.
  //
  // Phase 11.x (May 2026 — auto-start when seeded): when the
  // orchestrator boots in `loading_survivor` (initialSurvivorId
  // supplied), kick off `startMerges` once the cache is ready so
  // the pick_survivor screen never appears. Without this the
  // phase machine would sit in loading_survivor forever waiting
  // for a click that doesn't come.
  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      cluster.members.map((m) =>
        api.getContact(m.id).then((r) => r.contact).catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, SafeContact | null> = {};
      cluster.members.forEach((m, i) => {
        map[m.id] = results[i] ?? null;
      });
      setDetailCache(map);
      // Auto-start when seeded with initialSurvivorId.
      if (seedSurvivorId) {
        const survivor = map[seedSurvivorId];
        const nonSurvivors = cluster.members.filter(
          (m) => m.id !== seedSurvivorId,
        );
        if (survivor && nonSurvivors.length > 0) {
          setPhase({
            kind: "running",
            survivor,
            queue: nonSurvivors,
            cursor: 0,
            succeededIds: [],
          });
        } else if (nonSurvivors.length === 0) {
          toast.success("Nothing to merge — single-member cluster.");
          onComplete();
        }
        // If survivor failed to load (rare), the user falls through
        // to a manual flow on next interaction; we don't auto-start.
      }
      // Apply the auto-suggestion ONCE — only if the user hasn't
      // manually picked a different survivor in the meantime. Using
      // the per-member SafeContacts (not the cluster sides) so the
      // scoring sees every populated field, not just name+email+phone.
      if (!suggestionApplied) {
        const loaded = cluster.members
          .map((m) => map[m.id])
          .filter((c): c is SafeContact => c !== null && c !== undefined);
        if (loaded.length === cluster.members.length) {
          const suggestion = suggestSurvivor(loaded as SuggestableContactShape[]);
          if (suggestion && suggestion.suggestedId !== pickedSurvivorId) {
            setPickedSurvivorId(suggestion.suggestedId);
          }
          setSuggestionApplied(true);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cluster.members]);

  const formatName = (m: DuplicateContactSide): string => {
    const first = m.preferred_name ?? m.legal_first_name;
    const last = m.legal_last_name;
    const joined = [first, last].filter(Boolean).join(" ").trim();
    return joined === "" ? "(no name on file)" : joined;
  };

  async function startMerges() {
    if (!pickedSurvivorId) return;
    setPhase({ kind: "loading_survivor", survivorId: pickedSurvivorId });
    try {
      const cached = detailCache[pickedSurvivorId];
      const survivor = cached ?? (await api.getContact(pickedSurvivorId)).contact;
      // Selective merge: only the included (not-kept-separate)
      // non-survivors are queued. Excluded members stay as their own
      // separate records.
      const targetIds = new Set(
        selectMergeTargets(
          cluster.members.map((m) => m.id),
          pickedSurvivorId,
          excludedIds,
        ),
      );
      const nonSurvivors = cluster.members.filter((m) =>
        targetIds.has(m.id),
      );
      if (nonSurvivors.length === 0) {
        // Nothing left to merge (single-member cluster, or every other
        // member was kept separate).
        toast.success("Nothing to merge — every other record is kept separate.");
        onComplete();
        return;
      }
      setPhase({
        kind: "running",
        survivor,
        queue: nonSurvivors,
        cursor: 0,
        succeededIds: [],
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.status} — ${err.message}`
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setPhase({
        kind: "error",
        message,
        succeededIds: [],
        mergedAway: mergedAwayFromError(err),
      });
    }
  }

  async function advanceAfterMerge(survivorId: string) {
    if (phase.kind !== "running") return;
    const justMergedId = phase.queue[phase.cursor]?.id;
    const succeededIds = justMergedId
      ? [...phase.succeededIds, justMergedId]
      : phase.succeededIds;
    const nextCursor = phase.cursor + 1;
    if (nextCursor >= phase.queue.length) {
      // Done. Surface the cluster-wide undo toast.
      if (succeededIds.length > 0) {
        toast.success(
          `Merged ${succeededIds.length} contact${succeededIds.length === 1 ? "" : "s"} into ${formatName(
            cluster.members.find((m) => m.id === survivorId) ?? phase.queue[0]!,
          )}`,
          {
            // Auto-dismiss after ~18s (Baker 2026-06-27) instead of the
            // action-toast default of persistent. Still reversible from
            // History after it's gone.
            duration: MERGE_TOAST_DURATION_MS,
            action: {
              label: "Undo",
              onClick: () => undoMerges(succeededIds, () => onComplete()),
            },
          },
        );
      }
      onComplete();
      return;
    }
    // Re-fetch survivor so the next wizard iteration sees fields
    // that were just promoted from the previous merge (e.g. the
    // survivor's preferred_name might have just been filled in).
    const nextId = phase.queue[nextCursor]!.id;
    setPhase({ kind: "loading_next", succeededIds, nextId });
    try {
      const refreshed = (await api.getContact(survivorId)).contact;
      setPhase({
        kind: "running",
        survivor: refreshed,
        queue: phase.queue,
        cursor: nextCursor,
        succeededIds,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.status} — ${err.message}`
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setPhase({
        kind: "error",
        message,
        succeededIds,
        mergedAway: mergedAwayFromError(err),
      });
    }
  }

  async function undoMerges(mergedIds: string[], onAllUndone: () => void) {
    let undone = 0;
    let failed = 0;
    for (const id of mergedIds) {
      try {
        await api.unmergeContact(id);
        undone += 1;
      } catch (err) {
        failed += 1;
        console.error("[unmerge] failed for", id, err);
      }
    }
    if (failed === 0) {
      toast.success(
        `Undone — restored ${undone} contact${undone === 1 ? "" : "s"}`,
      );
    } else {
      toast.warning(
        `Undone ${undone} of ${mergedIds.length}. ${failed} couldn't be reversed — see /admin/data-hygiene/merged-contacts.`,
      );
    }
    onAllUndone();
  }

  // ─── Phase: pick_survivor / loading_survivor ───────────────────
  if (phase.kind === "pick_survivor" || phase.kind === "loading_survivor") {
    const isLoading = phase.kind === "loading_survivor";
    // Phase 11.x (May 2026 — inline mode): when seeded with
    // initialSurvivorId we boot in loading_survivor and the merge
    // run kicks off as soon as the detail cache resolves. Render a
    // tiny inline placeholder rather than a giant Modal/picker.
    if (inline && isLoading) {
      return (
        <Card data-testid="cluster-merge-orchestrator-loading">
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Loading merge review…
          </p>
        </Card>
      );
    }
    const body = (
      <Stack gap={12}>
          <div>
            <Pill variant="neutral">{cluster.match_type}</Pill>{" "}
            <span
              className="muted"
              style={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
              }}
            >
              {cluster.match_value}
            </span>
          </div>
          <p className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
            Pick the survivor. The other {memberCount - 1} contact
            {memberCount - 1 === 1 ? "" : "s"} will be merged into the
            survivor one at a time, with per-field review on each
            merge.
            {allowExclude && (
              <>
                {" "}
                Not everyone here is the same person? Untick anyone who’s
                a <strong>different person</strong> (e.g. a spouse who
                shares the phone) to keep them separate — only the ticked
                records merge.
              </>
            )}
          </p>

          {/* Phase B1 (May 2026): auto-suggestion banner. Renders only
              once every member's full SafeContact is loaded so the
              scoring uses real field counts, not just the cluster's
              name+email+phone preview. The user can ignore the
              suggestion by picking a different radio. */}
          {(() => {
            const loaded = cluster.members
              .map((m) => detailCache[m.id])
              .filter((c): c is SafeContact => c !== null && c !== undefined);
            if (loaded.length !== cluster.members.length) return null;
            const suggestion = suggestSurvivor(
              loaded as SuggestableContactShape[],
            );
            if (!suggestion) return null;
            const suggested = cluster.members.find(
              (m) => m.id === suggestion.suggestedId,
            );
            if (!suggested) return null;
            return (
              <div
                role="status"
                style={{
                  background: "rgba(74, 152, 198, 0.08)",
                  border: "1px solid rgba(74, 152, 198, 0.3)",
                  borderRadius: 6,
                  padding: "10px 12px",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <strong>Suggested survivor: {formatName(suggested)}</strong>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {suggestion.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
                {suggestion.suggestedId !== pickedSurvivorId && (
                  <button
                    type="button"
                    onClick={() =>
                      setPickedSurvivorId(suggestion.suggestedId)
                    }
                    style={{
                      marginTop: 8,
                      background: "var(--blue)",
                      color: "var(--white)",
                      border: "none",
                      padding: "4px 10px",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Use suggestion
                  </button>
                )}
              </div>
            );
          })()}
          <div className="cluster-orchestrator-survivor-list">
            {cluster.members.map((m) => {
              const detail = detailCache[m.id];
              const counts = detail ? countPopulatedFields(detail) : null;
              const checked = pickedSurvivorId === m.id;
              const isSurvivor = checked;
              const isExcluded = excludedIds.has(m.id);
              return (
                <label
                  key={m.id}
                  className={`cluster-orchestrator-survivor-row${checked ? " is-picked" : ""}`}
                  style={
                    allowExclude && isExcluded
                      ? { opacity: 0.55 }
                      : undefined
                  }
                >
                  <input
                    type="radio"
                    name="survivor"
                    value={m.id}
                    checked={checked}
                    onChange={() => {
                      setPickedSurvivorId(m.id);
                      // A record can't be both the survivor and "kept
                      // separate" — picking it as survivor re-includes it.
                      if (excludedIds.has(m.id)) {
                        setExcludedIds((prev) => {
                          const next = new Set(prev);
                          next.delete(m.id);
                          return next;
                        });
                      }
                    }}
                    disabled={isLoading}
                  />
                  <div className="cluster-orchestrator-survivor-row-body">
                    <div className="cluster-orchestrator-survivor-row-name">
                      {formatName(m)}
                      {allowExclude && isExcluded && (
                        <span
                          className="muted"
                          style={{ fontWeight: 500, marginLeft: 6 }}
                        >
                          · kept separate
                        </span>
                      )}
                    </div>
                    <div className="cluster-orchestrator-survivor-row-meta muted">
                      {[m.email, m.phone_primary, m.household_display_name]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </div>
                    {counts && (
                      <div className="cluster-orchestrator-survivor-row-count muted">
                        {counts.populated} of {counts.total} fields populated
                      </div>
                    )}
                    {/* Selective merge — include/exclude this record.
                        The survivor is always part of the merge, so it
                        has no toggle. Only shown for 3+ clusters. */}
                    {allowExclude && !isSurvivor && (
                      <label
                        className="cluster-orchestrator-survivor-row-include"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 6,
                          fontSize: 12,
                          cursor: isLoading ? "default" : "pointer",
                        }}
                        // Stop the row's survivor-radio <label> from
                        // also toggling when the checkbox is clicked.
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          disabled={isLoading}
                          onChange={(e) => {
                            const include = e.target.checked;
                            setExcludedIds((prev) => {
                              const next = new Set(prev);
                              if (include) next.delete(m.id);
                              else next.add(m.id);
                              return next;
                            });
                          }}
                        />
                        <span>
                          {isExcluded
                            ? "Different person — keep separate"
                            : "Merge this record in"}
                        </span>
                      </label>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {/* Selective-merge summary: spell out who's being kept
              separate so the steward sees the consequence before
              committing (no surprise merges). */}
          {allowExclude && excludedMembers.length > 0 && (
            <div
              className="muted"
              style={{ fontSize: 12, lineHeight: 1.5 }}
              data-testid="cluster-merge-kept-separate"
            >
              Keeping separate (not merged):{" "}
              <strong>
                {excludedMembers.map((m) => formatName(m)).join(", ")}
              </strong>
              .
            </div>
          )}
        </Stack>
    );
    // How many non-survivors will actually merge after exclusions.
    const mergeCount = includedNonSurvivors.length;
    const nothingToMerge = allowExclude && mergeCount === 0;
    const footer = (
      <Row gap={8}>
        <Button variant="ghost" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={startMerges}
          disabled={isLoading || !pickedSurvivorId || nothingToMerge}
        >
          {isLoading
            ? "Loading…"
            : nothingToMerge
              ? "Pick at least one record to merge"
              : `Review ${mergeCount} merge${mergeCount === 1 ? "" : "s"} →`}
        </Button>
      </Row>
    );
    if (inline) {
      return (
        <Card data-testid="cluster-merge-orchestrator-inline-pick">
          <Stack gap={12}>
            {body}
            {footer}
          </Stack>
        </Card>
      );
    }
    return (
      <Modal
        open
        onClose={isLoading ? () => undefined : onClose}
        title={`Merge cluster · ${memberCount} contact${memberCount === 1 ? "" : "s"}`}
        className="modal-wide"
        data-testid="cluster-merge-orchestrator"
        footer={footer}
      >
        {body}
      </Modal>
    );
  }

  // ─── Phase: error ──────────────────────────────────────────────
  if (phase.kind === "error") {
    const mergedAway = phase.mergedAway ?? null;
    const errorBody = (
      <>
        {/* A "record was already merged away" 404 shows the server's
            plain-English explanation, not the raw "404 — HTTP 404". The
            path forward (the surviving record) is offered in the footer,
            so this is never a dead end (Grant 2026-07-16). */}
        <ErrorBanner>{mergedAway ? mergedAway.message : phase.message}</ErrorBanner>
        {phase.succeededIds.length > 0 && (
          <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
            {phase.succeededIds.length} contact
            {phase.succeededIds.length === 1 ? "" : "s"} already merged
            successfully — those merges stand. To undo, visit the
            survivor's contact page or
            /admin/data-hygiene/merged-contacts.
          </p>
        )}
      </>
    );
    const errorFooter = (
      <Row gap={8}>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {mergedAway && (
          <a
            href={mergedAway.href}
            data-testid="merge-open-survivor"
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "var(--blue)",
              color: "var(--white)",
              textDecoration: "none",
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Open the surviving {mergedAway.entity}
          </a>
        )}
      </Row>
    );
    if (inline) {
      return (
        <Card data-testid="cluster-merge-orchestrator-inline-error">
          <Stack gap={8}>
            {errorBody}
            {errorFooter}
          </Stack>
        </Card>
      );
    }
    return (
      <Modal
        open
        onClose={onClose}
        title="Merge halted"
        className="modal-wide"
        footer={errorFooter}
      >
        {errorBody}
      </Modal>
    );
  }

  // ─── Phase: loading_next (between merges) ──────────────────────
  if (phase.kind === "loading_next") {
    if (inline) {
      return (
        <Card data-testid="cluster-merge-orchestrator-inline-loading-next">
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Picking up the next merge in the queue…
          </p>
        </Card>
      );
    }
    return (
      <Modal
        open
        onClose={() => undefined}
        title="Refreshing survivor…"
        className="modal-wide"
      >
        <p className="muted" style={{ fontSize: 13 }}>
          Picking up the next merge in the queue…
        </p>
      </Modal>
    );
  }

  // ─── Phase: running — render the wizard for the current pair ──
  const currentMerged = phase.queue[phase.cursor];
  const mergedDetail = currentMerged ? detailCache[currentMerged.id] : null;
  if (!currentMerged || !mergedDetail) {
    // Detail not yet cached — fetch it on demand.
    return (
      <PendingMergedFetch
        contactId={currentMerged?.id ?? ""}
        inline={inline}
        onResolve={(c) =>
          setDetailCache((prev) => ({ ...prev, [c.id]: c }))
        }
        onError={(message, err) =>
          setPhase({
            kind: "error",
            message,
            succeededIds: phase.succeededIds,
            mergedAway: mergedAwayFromError(err),
          })
        }
      />
    );
  }
  const stepLabel =
    phase.queue.length === 1
      ? null
      : `Step ${phase.cursor + 1} of ${phase.queue.length}`;
  const wizard = (
    <MergeReviewWizard
      survivor={phase.survivor}
      merged={mergedDetail}
      initialReason={`Cluster merge from duplicates queue (${cluster.match_type}: ${cluster.match_value})`}
      onMerged={(survivorId) => {
        void advanceAfterMerge(survivorId);
      }}
      onCancel={onClose}
    />
  );
  if (inline) {
    return (
      <div data-testid="cluster-merge-orchestrator-inline-wizard">
        {stepLabel && (
          <div
            className="muted"
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Review & merge · {stepLabel}
          </div>
        )}
        {wizard}
      </div>
    );
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={`Review &amp; merge${stepLabel ? ` · ${stepLabel}` : ""}`}
      className="modal-wide"
      data-testid="cluster-merge-orchestrator-wizard"
    >
      {wizard}
    </Modal>
  );
}

// Tiny child component that fetches a missing merged-side contact
// detail and resolves it back into the orchestrator's cache. Used in
// the rare case where the parallel prefetch failed for one member.
function PendingMergedFetch({
  contactId,
  inline = false,
  onResolve,
  onError,
}: {
  contactId: string;
  inline?: boolean;
  onResolve: (c: SafeContact) => void;
  onError: (message: string, err: unknown) => void;
}) {
  // Stable string deps for the effect — useMemo not necessary but
  // keeps the rule-of-hooks happy for future linters.
  const id = useMemo(() => contactId, [contactId]);
  useEffect(() => {
    let cancelled = false;
    api
      .getContact(id)
      .then((r) => {
        if (!cancelled) onResolve(r.contact);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${err.status} — ${err.message}`
            : err instanceof Error
              ? err.message
              : "Unknown error";
        onError(message, err);
      });
    return () => {
      cancelled = true;
    };
  }, [id, onResolve, onError]);
  if (inline) {
    return (
      <Card>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Fetching contact details…
        </p>
      </Card>
    );
  }
  return (
    <Modal open onClose={() => undefined} title="Loading merged contact…">
      <p className="muted">Fetching contact details…</p>
    </Modal>
  );
}
